import React, { useState, useEffect } from 'react';
import { Save, ChevronUp, ChevronDown, ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const LandingRoomSection = ({
  page,
  defaultTitle,
  defaultSubtitle,
  title,
  rooms = [],
  content = {},
  saveContent,
  openMediaUpload,
  isSaving
}) => {
  const initial = content?.[`${page}_rooms`]?.content || {};
  const resolvedDefaultTitle = defaultTitle || title || '';
  const resolvedDefaultSubtitle = defaultSubtitle || '';

  const [draft, setDraft] = useState({
    title: initial.title != null ? initial.title : resolvedDefaultTitle,
    subtitle: initial.subtitle != null ? initial.subtitle : resolvedDefaultSubtitle,
    roomIds: Array.isArray(initial.room_type_ids) ? [...initial.room_type_ids] : [],
    overrides: initial.overrides ? { ...initial.overrides } : {}
  });

  // Keep override images in sync after media upload updates global content
  useEffect(() => {
    const c = content?.[`${page}_rooms`]?.content;
    if (!c || !c.overrides) return;
    setDraft(prev => {
      const merged = { ...prev.overrides };
      Object.entries(c.overrides).forEach(([id, ov]) => {
        merged[id] = { ...(merged[id] || {}), ...ov };
      });
      return { ...prev, overrides: merged };
    });
  }, [content?.[`${page}_rooms`]?.content?.overrides]);

  const toggleRoom = (roomId, checked) => {
    setDraft(prev => {
      if (checked) {
        if (prev.roomIds.includes(roomId)) return prev;
        return { ...prev, roomIds: [...prev.roomIds, roomId] };
      }
      const nextOverrides = { ...prev.overrides };
      delete nextOverrides[roomId];
      return { ...prev, roomIds: prev.roomIds.filter(id => id !== roomId), overrides: nextOverrides };
    });
  };

  const moveRoom = (roomId, direction) => {
    setDraft(prev => {
      const arr = [...prev.roomIds];
      const idx = arr.indexOf(roomId);
      if (idx < 0) return prev;
      const nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= arr.length) return prev;
      [arr[idx], arr[nextIdx]] = [arr[nextIdx], arr[idx]];
      return { ...prev, roomIds: arr };
    });
  };

  const setOverrideField = (roomId, field, value) => {
    setDraft(prev => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        [roomId]: { ...(prev.overrides[roomId] || {}), [field]: value }
      }
    }));
  };

  const handleSave = () => {
    const cleanOverrides = {};
    draft.roomIds.forEach(roomId => {
      const ov = draft.overrides[roomId];
      if (!ov) return;
      const cleaned = {};
      ['image', 'description', 'bed', 'size'].forEach(field => {
        const val = ov[field];
        if (val != null && String(val).trim() !== '') cleaned[field] = val;
      });
      if (Object.keys(cleaned).length > 0) cleanOverrides[roomId] = cleaned;
    });

    if (typeof saveContent === 'function') {
      saveContent(page, 'rooms', 'section', {
        title: draft.title,
        subtitle: draft.subtitle,
        room_type_ids: draft.roomIds,
        overrides: cleanOverrides
      });
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-soft">
      <h3 className="font-display text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <ImageIcon className="w-5 h-5 text-emerald-600" />
        Kamar yang Tampil
      </h3>

      <div className="space-y-4">
        <div>
          <Label>Judul Section</Label>
          <Input
            value={draft.title}
            onChange={(e) => setDraft(prev => ({ ...prev, title: e.target.value }))}
            placeholder={resolvedDefaultTitle}
          />
        </div>
        <div>
          <Label>Subjudul</Label>
          <Input
            value={draft.subtitle}
            onChange={(e) => setDraft(prev => ({ ...prev, subtitle: e.target.value }))}
            placeholder={resolvedDefaultSubtitle}
          />
        </div>

        <div className="space-y-3">
          {(rooms || []).map((room) => {
            const roomId = room.room_type_id || room.id;
            const selected = draft.roomIds.includes(roomId);
            const ov = draft.overrides[roomId] || {};
            const idx = draft.roomIds.indexOf(roomId);

            return (
              <div key={roomId} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={(e) => toggleRoom(roomId, e.target.checked)}
                    className="h-4 w-4 text-emerald-600"
                  />
                  <span className="text-sm text-gray-700">Tampil</span>
                  <span className="font-medium text-gray-900">{room.name}</span>
                  <span className="text-xs text-gray-500">{selected ? `#${idx + 1}` : ''}</span>
                  {selected && (
                    <div className="ml-auto flex gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => moveRoom(roomId, -1)}
                        disabled={idx === 0}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => moveRoom(roomId, 1)}
                        disabled={idx === draft.roomIds.length - 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {selected && (
                  <div className="mt-3 space-y-3 pl-7">
                    <div>
                      <Label className="text-xs">Foto (URL)</Label>
                      <div className="flex gap-2">
                        <Input
                          value={ov.image || ''}
                          onChange={(e) => setOverrideField(roomId, 'image', e.target.value)}
                          placeholder="https://..."
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => openMediaUpload(`${page}_rooms`, 'overrides', 'image', room.room_type_id || room.id)}
                        >
                          Pilih/Unggah
                        </Button>
                      </div>
                      {ov.image && (
                        <img src={ov.image} alt={room.name} className="mt-2 rounded-lg h-24 object-cover" />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Deskripsi</Label>
                      <Textarea
                        value={ov.description || ''}
                        onChange={(e) => setOverrideField(roomId, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Kasur</Label>
                        <Input
                          value={ov.bed || ''}
                          onChange={(e) => setOverrideField(roomId, 'bed', e.target.value)}
                          placeholder="King Bed"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Ukuran</Label>
                        <Input
                          value={ov.size || ''}
                          onChange={(e) => setOverrideField(roomId, 'size', e.target.value)}
                          placeholder="32 m²"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Save className="w-4 h-4 mr-2" />Simpan Kamar
        </Button>
      </div>
    </div>
  );
};

export default LandingRoomSection;